import React from "react";
import { Phone, MapPin, Clock, Sparkles } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_36d91178-24f3-40fc-9720-d06acab0d2e6/artifacts/9kjaww63_IMG_1194.jpg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 overflow-hidden bg-white"
    >
      {/* Soft background accents */}
      <div
        aria-hidden
        className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#F5EFE0] blur-3xl opacity-70"
      />
      <div
        aria-hidden
        className="absolute -bottom-40 -left-32 w-[420px] h-[420px] rounded-full bg-[#FBF9F6] blur-3xl opacity-80"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left: Text */}
          <div className="lg:col-span-7 fade-up">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#F5EFE0] border border-[#9B8B3E]/20 mb-6"
              data-testid="hero-badge"
            >
              <Sparkles className="w-3.5 h-3.5 text-[#9B8B3E]" />
              <span className="font-latin text-[12px] tracking-[0.15em] uppercase text-[#857635]">
                Partur's Trusted Supermart
              </span>
            </div>

            <h1
              data-testid="hero-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1A1813]"
            >
              <span className="text-[#9B8B3E]">Quality Products.</span>
              <br />
              Unbeatable Prices.
              <br />
              <span className="italic text-[#4A6B3A]">
                Right in Partur.
              </span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-6 text-base sm:text-lg text-[#5C5646] max-w-xl leading-relaxed font-latin"
            >
              Grocery, Clothes, Stationary, Bags, Utensils and Gift Articles —
              everything under one roof. Every essential your family needs,
              with trust and affordability built in.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="#categories"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#9B8B3E] text-white font-medium hover:bg-[#857635] transition-all shadow-[0_8px_24px_rgba(155,139,62,0.25)] hover:shadow-[0_12px_32px_rgba(155,139,62,0.35)] hover:-translate-y-0.5 font-latin"
              >
                Explore Categories
              </a>
              <a
                href="#location"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border border-[#E5E1D5] text-[#1A1813] font-medium hover:border-[#9B8B3E] hover:text-[#9B8B3E] transition-all font-latin"
              >
                <MapPin className="w-4 h-4" />
                Find Our Store
              </a>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-xl">
              <InfoChip
                icon={<Clock className="w-4 h-4" />}
                label="Hours"
                value="9 AM – 10 PM"
                testid="hero-chip-hours"
              />
              <InfoChip
                icon={<Phone className="w-4 h-4" />}
                label="Call"
                value="7278919919"
                testid="hero-chip-phone"
              />
              <InfoChip
                icon={<MapPin className="w-4 h-4" />}
                label="Location"
                value="Partur, 431501"
                testid="hero-chip-location"
              />
            </div>
          </div>

          {/* Right: Logo showcase */}
          <div className="lg:col-span-5 relative fade-up">
            <div className="relative mx-auto w-full max-w-md aspect-square">
              {/* Decorative rings */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#F5EFE0] to-[#FBF9F6] border border-[#E5E1D5]" />
              <div className="absolute inset-6 rounded-full border border-[#9B8B3E]/15" />
              <div className="absolute inset-12 rounded-full border border-[#9B8B3E]/10" />

              <div className="absolute inset-0 flex items-center justify-center p-10 animate-float-soft">
                <img
                  src={LOGO_URL}
                  alt="R Maart Logo"
                  className="w-full h-full object-contain drop-shadow-[0_12px_40px_rgba(155,139,62,0.18)]"
                  data-testid="hero-logo"
                />
              </div>

              {/* Floating offer pills */}
              <div className="absolute -top-2 -left-2 sm:-top-6 sm:-left-6 bg-white border border-[#E5E1D5] rounded-2xl px-4 py-3 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
                <div className="font-latin text-[10px] uppercase tracking-[0.25em] text-[#5C5646]">
                  Offer
                </div>
                <div className="font-display text-lg text-[#9B8B3E] font-semibold">
                  Up to 50% Off
                </div>
              </div>

              <div className="absolute -bottom-2 -right-2 sm:-bottom-6 sm:-right-6 bg-[#9B8B3E] text-white rounded-2xl px-4 py-3 shadow-[0_8px_30px_rgba(155,139,62,0.3)]">
                <div className="font-latin text-[10px] uppercase tracking-[0.25em] opacity-80">
                  Special
                </div>
                <div className="font-display text-lg font-semibold">
                  Buy 1 Get 1 Free
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoChip({ icon, label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center gap-3 bg-[#FBF9F6] border border-[#E5E1D5] rounded-xl px-4 py-3 hover:border-[#9B8B3E]/40 transition-colors"
    >
      <div className="w-9 h-9 rounded-lg bg-white border border-[#E5E1D5] flex items-center justify-center text-[#9B8B3E]">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5646] font-latin">
          {label}
        </div>
        <div className="text-sm text-[#1A1813] truncate font-latin font-medium">
          {value}
        </div>
      </div>
    </div>
  );
}
